import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import PageHeader from '@/Components/PageHeader';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { ArrowLeftIcon, PencilSquareIcon, EnvelopeIcon, TrashIcon, PaperClipIcon, GlobeAltIcon, PhoneIcon } from '@heroicons/react/24/outline';

function InfoRow({ label, value, children }) {
    if (!value && !children) return null;
    return (
        <div className="py-3 border-b border-slate-100 last:border-0 flex justify-between items-center">
            <span className="text-sm text-slate-500">{label}</span>
            {children || <span className="text-sm font-medium text-slate-800">{value}</span>}
        </div>
    );
}

export default function Show({ visitor, flash }) {
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    return (
        <AppLayout>
            <Head title={`Visitor: ${visitor.personal_contact_name || visitor.company_name || 'Details'}`} />
            <PageHeader
                title={visitor.personal_contact_name || 'Unknown Visitor'}
                subtitle={visitor.company_name || 'Individual Visitor'}
                actions={
                    <div className="flex gap-2">
                        <Button href="/visitors" variant="outline" size="sm">
                            <ArrowLeftIcon className="w-4 h-4" /> Back
                        </Button>
                        <Button href={`/visitors/${visitor.id}/edit`} variant="outline" size="sm">
                            <PencilSquareIcon className="w-4 h-4" /> Edit
                        </Button>
                        {visitor.email && (
                            <Button href={`/visitors/${visitor.id}/send-email`} size="sm">
                                <EnvelopeIcon className="w-4 h-4" /> Send Email
                            </Button>
                        )}
                        <Button variant="danger" size="sm" onClick={() => setDeleteConfirm(true)}>
                            <TrashIcon className="w-4 h-4" /> Delete
                        </Button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Personal Information */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Personal Information</h3>
                    </div>
                    <CardBody>
                        <InfoRow label="Name" value={visitor.personal_contact_name} />
                        <InfoRow label="Email" value={visitor.email} />
                        <InfoRow label="Type">
                            {visitor.type && (
                                <Badge variant={visitor.type === 'Supplier' ? 'purple' : 'info'}>
                                    {visitor.type}
                                </Badge>
                            )}
                        </InfoRow>
                        <InfoRow label="Exhibition" value={visitor.exhibition?.name} />
                        <InfoRow label="Registered" value={new Date(visitor.created_at).toLocaleDateString()} />
                        <InfoRow label="Registered By" value={visitor.creator?.name || (visitor.created_by ? 'Admin' : 'Self-registered')} />
                    </CardBody>
                </Card>

                {/* Company Information */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Company Information</h3>
                    </div>
                    <CardBody>
                        <InfoRow label="Company" value={visitor.company_name} />
                        <InfoRow label="Country" value={visitor.country} />
                        <InfoRow label="Fax" value={visitor.fax} />
                        {visitor.website_url && (
                            <InfoRow label="Website">
                                <a href={visitor.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-800 hover:underline flex items-center gap-1">
                                    <GlobeAltIcon className="w-4 h-4" />
                                    {visitor.website_url}
                                </a>
                            </InfoRow>
                        )}
                    </CardBody>
                </Card>

                {/* Phone Numbers */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Phone Numbers</h3>
                    </div>
                    <CardBody>
                        <InfoRow
                            label="Telephone"
                            value={visitor.telephone ? `${visitor.telephone_country_code || ''} ${visitor.telephone}`.trim() : null}
                        />
                        <InfoRow
                            label="Mobile"
                            value={visitor.mobile_number ? `${visitor.mobile_country_code || ''} ${visitor.mobile_number}`.trim() : null}
                        />
                        <InfoRow
                            label="WhatsApp"
                            value={visitor.whatsapp_number ? `${visitor.whatsapp_country_code || ''} ${visitor.whatsapp_number}`.trim() : null}
                        />
                        {!visitor.telephone && !visitor.mobile_number && !visitor.whatsapp_number && (
                            <p className="text-sm text-slate-400 py-3">No phone numbers on record.</p>
                        )}
                    </CardBody>
                </Card>

                {/* Attachments */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Attachments</h3>
                    </div>
                    <CardBody>
                        {visitor.attachments && visitor.attachments.length > 0 ? (
                            <div className="space-y-2">
                                {visitor.attachments.map((att, i) => (
                                    <a
                                        key={i}
                                        href={`/storage/${att.path}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
                                    >
                                        <PaperClipIcon className="w-5 h-5 text-slate-400" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-700 truncate">{att.name}</p>
                                            <p className="text-xs text-slate-400">
                                                {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''} {att.type ? `- ${att.type}` : ''}
                                            </p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400 py-3">No attachments uploaded.</p>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                show={deleteConfirm}
                onClose={() => setDeleteConfirm(false)}
                title="Delete Visitor"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete <strong>{visitor.personal_contact_name || 'this visitor'}</strong>?
                        This action cannot be undone and will remove all associated attachments and email logs.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/visitors/${visitor.id}`);
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
